import { useState, useMemo, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Breadcrumb, message } from "antd";
import dayjs from "dayjs";
import { ApolloError } from "@apollo/client";

import LoadingOverlay from "components/loading-overlay/loading-overlay";
import GuideCard from "components/guide-card/guide-card";
import HandoverTimeline from "components/handover-timeline/handover-timeline";
import HandoverDeviceDetectionSummary from "components/handover-device-detection-summary/handover-device-detection-summary";
import HandoverDeviceDetectionResults from "components/handover-device-detection-results/handover-device-detection-results";
import HandoverPublishedDevices from "components/handover-published-devices/handover-published-devices";
import HandoverAdditionalResources from "components/handover-additional-resources/handover-additional-resources";
import HandoverActivity from "components/handover-activity/handover-activity";
import HandoverSent from "components/handover-sent/handover-sent";

import { Handover, Guides, Properties } from "graphql/generated/graphql";
import { useGetHandoverByIdQuery } from "graphql/queries/get-handover-by-id.generated";
import { useUpdateHandoverByIdMutation } from "graphql/mutations/update-handover-by-id.generated";
import { DeviceDetectionJobStatus, GuideStatus, HandoverStatus } from "constants/field-values";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import { useDeviceDetectionCore } from "hooks/useDeviceDetectionCore";
import routeNames from "constants/route-names";
import { getPropertyAddress } from "utils/helpers";

const AnonBanner = ({ onCreateAccount }: { onCreateAccount: () => void }) => (
	<div className="hd-anon-banner">
		<img src={`${process.env.PUBLIC_URL}/assets/images/icons/info-circle-2.svg`} height={32} alt="" />
		<div className="hd-anon-banner-content">
			<div className="hd-anon-banner-title">ACCOUNT CREATION REQUIRED</div>
			<p className="hd-anon-banner-text">
				You'll need to create an account before completing this handover. Once your account is set up, you can proceed
				with submitting your information.
			</p>
		</div>
		<button className="highnote-button" onClick={onCreateAccount}>
			Create account or sign in
		</button>
	</div>
);

/**
 * Builds a denormalized copy of the handover and its relations for public (anonymous) access.
 * Stored in public_data_snapshot (jsonb) at submission time — no joins required for anon users.
 */
const buildPublicDataSnapshot = (handover: any, status: string, updatedAt: string) => {
	// Deep clone via JSON serialization, stripping Apollo __typename metadata in the process
	const snapshot = JSON.parse(
		JSON.stringify(handover, (key, value) => {
			if (key === "__typename") return undefined;
			return value;
		})
	);

	// Override with the values being set in the mutation
	snapshot.status = status;
	snapshot.updated_at = updatedAt;

	return snapshot;
};

// Edit permission matrix:
//   VENDOR   + PENDING_VENDOR | VENDOR_DRAFT  → canEdit = true   vendor completing
//   CUSTOMER + PENDING_CUSTOMER               → canEdit = true   customer reviewing
//   CUSTOMER + PENDING_VENDOR                 → summary editable (project name/description/notes only)
//   VENDOR   + PENDING_CUSTOMER               → canEdit = false  read-only, awaiting customer
//   any      + PUBLISHED | CANCELLED          → canEdit = false  terminal
//   ANON                                      → canEdit = false  account required

const HandoverDetails = () => {
	const navigate = useNavigate();
	const { handoverId } = useParams<{ handoverId: string }>();
	const [searchParams] = useSearchParams();
	const emailParam = searchParams.get("email");
	const {
		isUserAnon: isAnon, // True when the page is accessed via anonymous link (no account yet)
		loggedUserValue
	} = useLoggedUser();
	const [isActioning, setIsActioning] = useState(false);
	const [handoverSubmitted, setHandoverSubmitted] = useState(false);

	const handleOnError = useCallback((error: ApolloError) => {
		console.error("Error fetching Handover By Id:", error);
	}, []);

	const handleOnComplete = useCallback(data => {
		console.log("On Complete Handover By Id data: ", data);
	}, []);

	const { data, loading } = useGetHandoverByIdQuery({
		variables: { id: handoverId!, isAnon },
		skip: !handoverId,
		errorPolicy: "none",
		fetchPolicy: "network-only",
		nextFetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onError: handleOnError,
		onCompleted: handleOnComplete
	});

	const handover = data?.handover_by_pk;

	const handleUpdateHandoverError = useCallback((error: ApolloError) => {
		console.error("Update Handover error:", error);
	}, []);

	const handleUpdateHandoverComplete = useCallback(data => {
		console.log("Update Handover completed: ", data);
	}, []);

	const [updateHandoverByIdMutation, { loading: loadingUpdateHandover }] = useUpdateHandoverByIdMutation({
		errorPolicy: "none",
		fetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onCompleted: handleUpdateHandoverComplete,
		onError: handleUpdateHandoverError,
		refetchQueries: ["getHandoverById", "getHandoverByOrganizationIds"]
	});

	const isVendor = !isAnon && loggedUserValue.organization_id === handover?.vendor_organization_id;
	const isCustomer = !isAnon && !isVendor;
	const requesterIsVendor = handover?.requester?.organization_id === handover?.vendor_organization_id;

	const isPendingVendor = handover?.status === HandoverStatus.PENDING_VENDOR;
	const isVendorDraft = handover?.status === HandoverStatus.VENDOR_DRAFT;
	const isPendingCustomer = handover?.status === HandoverStatus.PENDING_CUSTOMER;
	const isPublished = handover?.status === HandoverStatus.PUBLISHED;
	const isCancelled = handover?.status === HandoverStatus.CANCELLED;
	const isTerminal = isPublished || isCancelled;

	const canEdit =
		!isAnon && !isTerminal && ((isVendor && (isPendingVendor || isVendorDraft)) || (isCustomer && isPendingCustomer));

	const deviceDetectionJob = handover?.device_detection_jobs?.[0];
	const hasDeviceDetection =
		deviceDetectionJob &&
		(deviceDetectionJob.status === DeviceDetectionJobStatus.DRAFT ||
			deviceDetectionJob.status === DeviceDetectionJobStatus.PUBLISHED);

	// When the vendor has submitted and the handover is awaiting customer review,
	// anon users see the snapshot captured at submission time instead of live relations.
	const snapshot = isAnon && isPendingCustomer ? (handover?.public_data_snapshot ?? null) : null;

	const guidesCount = snapshot ? (snapshot.guides?.length ?? 0) : (handover?.guides?.length ?? 0);

	const core = useDeviceDetectionCore({
		onBack: () => {
			return;
		},
		onJobReady: () => {
			return;
		},
		handoverId: handoverId,
		propertyId: handover?.property_id,
		detectionJobId: deviceDetectionJob?.id,
		initialProperty: (handover?.property as Properties) ?? null
	});

	const { isLoading, job, allInstalledProductsSkipped } = core;

	const canVendorSubmit =
		isVendor &&
		canEdit &&
		deviceDetectionJob &&
		deviceDetectionJob.status === DeviceDetectionJobStatus.PUBLISHED &&
		!allInstalledProductsSkipped;

	const pageTitle = useMemo(() => {
		if (isCustomer && isPendingCustomer) return "Review Handover";
		if (isTerminal) return "View Handover";
		return "Complete Handover";
	}, [isCustomer, isPendingCustomer, isTerminal]);

	/**
	 * Vendor submits → PENDING_CUSTOMER.
	 * Captures a public_data_snapshot of the handover and its relations at this point in time
	 * so anonymous (customer) users can view all data without direct DB access.
	 */
	const handleVendorSubmit = useCallback(async () => {
		// Devices and Equipment is the only required section to submit.
		// Guides and Additional Resources are optional.
		if (!hasDeviceDetection) {
			message.success("Devices and Equipment section is required to submit.");
			return;
		}
		setIsActioning(true);

		const newStatus = HandoverStatus.PENDING_CUSTOMER;
		const newUpdatedAt = dayjs().format("YYYY-MM-DDTHH:mm:ssZ");

		try {
			updateHandoverByIdMutation({
				variables: {
					id: handoverId!,
					set: {
						status: newStatus,
						updated_at: newUpdatedAt,
						public_data_snapshot: buildPublicDataSnapshot(handover, newStatus, newUpdatedAt)
					}
				}
			});

			message.success("Handover submitted for customer review.");
			setHandoverSubmitted(true);
		} catch {
			message.error("Failed to submit. Please try again.");
		} finally {
			setIsActioning(false);
		}
	}, [handoverId, navigate, hasDeviceDetection, handover]);

	/**
	 * Customer approves → PUBLISHED.
	 */
	const handleCustomerPublish = useCallback(async () => {
		setIsActioning(true);
		try {
			updateHandoverByIdMutation({
				variables: {
					id: handoverId!,
					set: { status: HandoverStatus.PUBLISHED, published_at: dayjs().format("YYYY-MM-DDTHH:mm:ssZ") }
				}
			});
			message.success("Handover approved and published.");
			setHandoverSubmitted(true);
		} catch {
			message.error("Failed to publish. Please try again.");
		} finally {
			setIsActioning(false);
		}
	}, [handover, handoverId, navigate]);

	const handleEditSummary = useCallback(() => {
		navigate(routeNames.HANDOVER_DETAILS_EDIT.replace(":handoverId", handoverId!));
	}, [handoverId, navigate]);

	const handleAddDevices = useCallback(() => {
		if (!handover) return;

		let path = routeNames.HANDOVER_DEVICE_DETECTION_ADD;

		if (deviceDetectionJob) {
			const jobId = deviceDetectionJob.id;
			path = routeNames.HANDOVER_DEVICE_DETECTION_DETAILS.replace(":jobId", jobId);
		}

		navigate(`${path}?handoverId=${handoverId}&propertyId=${handover?.property_id}`);
	}, [handoverId, handover?.property_id, deviceDetectionJob, navigate]);

	const guidesEnabled = useMemo(() => {
		if (isAnon) return false;

		if (deviceDetectionJob) {
			const isPublished = deviceDetectionJob.status === DeviceDetectionJobStatus.PUBLISHED;
			return isPublished;
		}

		return false;
	}, [isAnon, deviceDetectionJob]);

	const handleAddGuide = useCallback(() => {
		if (guidesEnabled) navigate(`${routeNames.GUIDE_ADD}?handoverId=${handoverId}`);
	}, [guidesEnabled, handoverId, navigate]);

	const handleOnGuideCardClick = useCallback(
		(guideId, guideStatus) => {
			if (guideStatus === GuideStatus.draft || guideStatus === GuideStatus.processing) {
				navigate(routeNames.GUIDE_DETAILS_EDIT.replace(":guideId", guideId));
				return;
			}

			navigate(routeNames.GUIDE_DETAILS.replace(":guideId", guideId));
		},
		[navigate]
	);

	const participants = useMemo(() => {
		if (!handover) return null;

		const organization = requesterIsVendor ? handover.customer_organization : handover.vendor_organization;

		const orgInfo = organization?.name ?? "";
		const orgSubInfo = handover.user_invitations?.length
			? handover.user_invitations[0].email
			: (organization?.email ?? "");

		const requesterInfo = handover.requester
			? `${handover.requester.first_name} ${handover.requester.last_name} (${handover.requester.organization?.name})`
			: "";
		const requesterSubInfo = handover.requester?.email ?? "";

		const contactInfo = requesterIsVendor ? orgInfo : requesterInfo;
		const contactSubInfo = requesterIsVendor ? orgSubInfo : requesterSubInfo;
		const vendorInfo = requesterIsVendor ? requesterInfo : orgInfo;
		const vendorSubInfo = requesterIsVendor ? requesterSubInfo : orgSubInfo;

		return (
			<>
				<div className="hd-info-row">
					<span className="hd-info-label">Contact:</span>
					<div>
						<div className="hd-info-value">{contactInfo}</div>
						<div className="hd-info-sub-value">{contactSubInfo}</div>
					</div>
				</div>

				<div className="hd-info-row">
					<span className="hd-info-label">Vendor:</span>
					<div>
						<div className="hd-info-value">{vendorInfo}</div>
						<div className="hd-info-sub-value">{vendorSubInfo}</div>
					</div>
				</div>
			</>
		);
	}, [handover, requesterIsVendor]);

	if (loading || loadingUpdateHandover || !handover) return <LoadingOverlay />;

	return (
		<div className="products-details-screen-container">

			<Breadcrumb
				className="products-breadcrumbs"
				items={[{ title: "Handovers", onClick: () => navigate(routeNames.HANDOVERS) }, { title: pageTitle }]}
			/>

			<div className="hd-page">
				{isAnon && (
					<AnonBanner
						onCreateAccount={() =>
							navigate(emailParam ? `${routeNames.SIGNUP}?email=${encodeURIComponent(emailParam)}` : routeNames.SIGNUP)
						}
					/>
				)}

				{/* ── Title row + CTAs ── */}
				<div className="hd-title-actions">
					{/* Vendor completing */}
					{isVendor && canEdit && (
						<>
							<button className="ha-cancel-btn" onClick={() => navigate(routeNames.HANDOVERS)}>
								Cancel
							</button>
							<button
								className="ha-modal-save-btn"
								disabled={!canVendorSubmit || isActioning}
								onClick={handleVendorSubmit}
							>
								{isActioning ? "Submitting…" : "Submit"}
							</button>
						</>
					)}

					{/* Customer reviewing — both buttons visible and active per Figma */}
					{isCustomer && isPendingCustomer && (
						<>
							{/* Reject: spec not defined for v1, disabled placeholder */}
							<button className="hd-reject-btn" disabled title="Reject not available in v1">
								Reject
							</button>
							<button className="highnote-button hd-approve-btn" disabled={isActioning} onClick={handleCustomerPublish}>
								{isActioning ? (
									"Publishing…"
								) : (
									<>
										<img alt={`checkmark-icon`} src={`${process.env.PUBLIC_URL}/assets/images/icons/checkmark.svg`} />
										Approve and publish
									</>
								)}
							</button>
						</>
					)}
				</div>

				{/* ── Timeline ── */}
				<HandoverTimeline handover={(isAnon ? snapshot : handover) as Handover} />

				{handoverSubmitted ? (
					<HandoverSent handover={handover as Handover} />
				) : (
					<>
						{/* ── Info card ── */}
						<div className="hd-info-card">
							{((isVendor && isVendorDraft) || (isCustomer && isPendingVendor)) && (
								<button className="hd-info-card-edit-btn" onClick={handleEditSummary} title="Edit project details">
									<img alt="edit-icon" src={`${process.env.PUBLIC_URL}/assets/images/icons/edit-pencil-purple.svg`} />
								</button>
							)}
							<div className="hd-info-grid">
								{handover?.property && (
									<div className="hd-info-row">
										<span className="hd-info-label">Property:</span>
										<div>
											<div className="hd-info-value">{handover?.property.name}</div>
											{getPropertyAddress(handover?.property.address, handover?.property.standardized_address) && (
												<div className="hd-info-sub-value">
													{getPropertyAddress(handover?.property.address, handover?.property.standardized_address)}
												</div>
											)}
										</div>
									</div>
								)}
								<div className="hd-info-row">
									<span className="hd-info-label">Project:</span>
									<div className="hd-info-value">{handover?.project_name}</div>
								</div>

								{participants}
							</div>

							{handover?.project_description && (
								<div className="hd-info-row-full">
									<span className="hd-info-label">Description:</span>
									<div className="hd-info-description">{handover?.project_description}</div>
								</div>
							)}
							{handover?.requester_notes && (
								<div className="hd-info-row-full">
									<span className="hd-info-label">Notes:</span>
									<div className="hd-info-description">{handover?.requester_notes}</div>
								</div>
							)}
						</div>

						{/* ── Devices and Equipment ── */}
						<div className={`hd-section${isAnon && !snapshot ? " hd-section--disabled" : ""}`}>
							<div className="hd-section-title-row">
								<div className="hd-section-title">
									<div className="hd-section-title-number">1.</div> Devices and Equipment
								</div>
								{hasDeviceDetection && deviceDetectionJob.status === DeviceDetectionJobStatus.DRAFT ? (
									<img
										alt={`edit-attachment-icon`}
										src={`${process.env.PUBLIC_URL}/assets/images/icons/edit-pencil-purple.svg`}
										className={"hd-section-title-icon"}
										onClick={handleAddDevices}
									/>
								) : null}
							</div>

							{handover?.vendor_device_notes && (
								<div className="hd-section-card hd-device-notes">
									<div className="hd-info-row-full">
										<span className="hd-info-label">Comments:</span>
										<div className="hd-info-description">{handover?.vendor_device_notes}</div>
									</div>
								</div>
							)}

							{snapshot ? (
								// Anon + PENDING_CUSTOMER: render installed products from the snapshot
								<HandoverPublishedDevices handover={snapshot as Handover} readOnly />
							) : isAnon || (canEdit && !hasDeviceDetection) ? (
								<div className="hd-section-card">
									<p className="hd-section-description">
										Upload documentation of all devices and components installed as part of this project.
										Include photos, videos, or documents (e.g. field notes, work orders, invoices) that contain specific details
										such as make, model, serial numbers, and installation locations. Our AI will handle the rest,
										and you’ll have a chance to review everything before submitting this Handover for customer review.
									</p>
									<button className="hd-add-btn" disabled={isAnon} onClick={isAnon ? undefined : handleAddDevices}>
										{isAnon ? "Continue" : "Start"}
									</button>
								</div>
							) : job ? (
								<>
									{isCustomer ||
									handover?.status === HandoverStatus.PENDING_CUSTOMER ||
									handover?.status === HandoverStatus.PUBLISHED ? null : (
										<HandoverDeviceDetectionSummary
											aiResponse={job.ai_response}
											detectedInstalledProducts={job.detected_installed_products}
											productsGroupedByType={core.productsGroupedByProductType}
											allInstalledProductsSkipped={allInstalledProductsSkipped}
											isLoading={isLoading}
										/>
									)}

									{job.status === DeviceDetectionJobStatus.PUBLISHED ? (
										<HandoverPublishedDevices handover={handover as Handover} readOnly={!canEdit} />
									) : (
										<HandoverDeviceDetectionResults deviceDetectionCore={core} readOnly />
									)}
								</>
							) : (
								<div className="hd-section-card">
									<p className="hd-section-empty">No devices were added to this handover.</p>
								</div>
							)}
						</div>

						{/* ── How-To Guides ── */}
						<div className={`hd-section${guidesEnabled ? "" : " hd-section--disabled"}`}>
							<div className="hd-section-title">
								<div className="hd-section-title-number">2.</div> How-To Guides
							</div>
							<div className="hd-section-card">
								{(canEdit || isAnon) && (
									<p className="hd-section-description">
										Upload videos of any educational or training procedures to help the property team understand how to
										use or maintain the installed systems. Our AI will automatically turn these into step-by-step
										guides.
									</p>
								)}
								{guidesCount > 0 && (
									<div className="hd-guides-list">
										{(snapshot ? snapshot.guides : handover?.guides!).map(guide => (
											<GuideCard
												key={guide.id}
												guide={guide as Guides}
												onClick={() => handleOnGuideCardClick(guide.id, guide.status)}
											/>
										))}
									</div>
								)}
								{!isAnon && guidesCount === 0 && !canEdit && (
									<p className="hd-section-empty">No guides were added to this handover.</p>
								)}
								{(canEdit || isAnon) && (
									<button
										className="hd-add-btn"
										disabled={!guidesEnabled}
										onClick={guidesEnabled ? handleAddGuide : undefined}
									>
										{!isAnon && guidesCount > 0 ? "Create another guide" : isAnon ? "Continue" : "Create a guide"}
									</button>
								)}
							</div>
						</div>

						{/* ── Additional Resources ── */}
						<HandoverAdditionalResources
							propertyId={handover.property_id!}
							handoverId={handover.id}
							propertyDocuments={snapshot ? snapshot.property_documents : handover.property_documents || []}
							canEdit={canEdit}
							isAnon={isAnon}
						/>

						<HandoverActivity handoverId={handover.id} />
					</>
				)}
			</div>
		</div>
	);
};

export default HandoverDetails;
